import { Hub } from 'aws-amplify'

import { HUB_AUTH_EVENT } from '@enums/amplify-hub-states'

// TODO: aws-amplify/lib-esm contains this Type but it's not exported — is there another way
// to get ahold of it instead creating it again here?
type HubEvent = {
  payload: {
    event: string
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    data?: any
  }
}

type HubData = HubEvent['payload']['data']

export type HubEventCallback = (hubData?: HubData) => void

type EventHandlers = {
  onSignIn: HubEventCallback
  onSignOut: HubEventCallback
  onSignInFailure: HubEventCallback
  onSignUpFailure: HubEventCallback
  onForgotPassword: HubEventCallback
  onForgotPasswordFailure: HubEventCallback
}

/**
 * AWS Amplify offers a `Hub` package to listen for any events that Amplify
 * can handle. in our case it's used for 'Auth' related items — specifically
 * `signIn` and `signOut`.
 *
 * exposing an API here to manipulate whether the real Amplify Hub is used vs
 * a mocked version for tests
 *
 * setting defaults as we are only interested in 'auth' events
 */

export async function initHubAuthListen(eventHandlers: EventHandlers) {
  if (window.Cypress) {
    const HubMocked = await import(
      './amplify-mock-implementations/mocked-amplify-hub'
    )

    const hubAuthListener = listen(eventHandlers)
    HubMocked.default.listen('auth', hubAuthListener)
    return
  }

  // wire-up the Amplify Hub listener for Cognito-related auth events
  const hubAuthListener = listen(eventHandlers)
  Hub.listen('auth', hubAuthListener)
}

export function listen(eventHandlers: EventHandlers) {
  return async (data: HubEvent): Promise<void> => {
    const payload = data.payload
    switch (payload.event) {
      /**
       * we do not listen for the SIGN_UP event — see explanation in:
       * src/api/cognito/sign-up.ts
       *
       * intentionally not including a default case because of the above
       */
      case HUB_AUTH_EVENT.SIGN_IN:
        await eventHandlers.onSignIn(payload.data)
        break
      case HUB_AUTH_EVENT.SIGN_IN_FAILURE:
        eventHandlers.onSignInFailure(payload.data)
        break
      case HUB_AUTH_EVENT.SIGN_UP_FAILURE:
        eventHandlers.onSignUpFailure(payload.data)
        break
      case HUB_AUTH_EVENT.SIGN_OUT:
        eventHandlers.onSignOut()
        break
      case HUB_AUTH_EVENT.FORGOT_PASSWORD_SUBMIT:
        eventHandlers.onForgotPassword()
        break
      case HUB_AUTH_EVENT.FORGOT_PASSWORD_SUBMIT_FAILURE:
        eventHandlers.onForgotPasswordFailure(payload.data)
        break
      default:
        break
    }
  }
}
